import { type ReactNode } from "react";
import { StatusBadge, formatMeasured } from "../../components/index.ts";
import {
  formatAgeSeconds,
  formatCount,
  formatMillis,
  quantityMeasure,
  type SystemHealth,
} from "./vocab.ts";

/**
 * Runtime health tiles (task 11.15; design 12.2 "shard latency, hub connections, verification lag").
 * These are the "how is the plane running" signals that are neither acceptance nor a processing
 * outcome:
 *
 * - SHARD LATENCY p50 / p95 (ms), from the contract `Quantity` pair — a percentile is an ESTIMATE,
 *   so it carries "est." via the shared `Quantity` → `Measure` mapping; a null value reads "n/a"
 *   (unverifiable), never a reassuring zero.
 * - HUB CONNECTIONS, an exact count of live stream subscribers. This comes from the REST snapshot
 *   (there is no `system` stream topic to push it), so it refreshes on the poll.
 * - VERIFICATION LAG in seconds (`verification_lag_s`), the audit-chain verification backlog. A null
 *   lag is unverifiable (never green); a growing lag is a warn.
 *
 * Nothing here is green unless genuinely healthy (design 12.3): an unverifiable latency or lag shows
 * as unknown, not healthy.
 */

export interface RuntimeHealthProps {
  readonly shardLatency: SystemHealth["shard_latency_ms"];
  readonly hub: SystemHealth["hub"];
  readonly verificationLagS: SystemHealth["verification_lag_s"];
}

/** A verification lag past this many seconds is surfaced as a warn (the chain is falling behind). */
const VERIFICATION_LAG_WARN_S = 300;

export function RuntimeHealth(props: RuntimeHealthProps): ReactNode {
  const { shardLatency, hub, verificationLagS } = props;

  const lagLevel =
    verificationLagS === null
      ? "unknown"
      : verificationLagS > VERIFICATION_LAG_WARN_S
        ? "warn"
        : "healthy";
  const lagLabel =
    verificationLagS === null
      ? "Unverifiable"
      : verificationLagS > VERIFICATION_LAG_WARN_S
        ? "Falling behind"
        : "Current";

  return (
    <section className="sys-region" aria-label="Runtime health">
      <h2 className="sys-region__title">Runtime health</h2>
      <p className="sys-region__note sys-faint">
        Shard write latency, live-stream connections, and how far behind audit-chain verification is.
      </p>

      <div className="sys-tiles" role="group" aria-label="Runtime health tiles">
        <article className="sys-tile" aria-label="Shard latency p50">
          <h3 className="sys-tile__label">Shard latency p50</h3>
          <p className="sys-tile__value">
            {shardLatency.p50.value === null
              ? "n/a"
              : formatMeasured({ value: formatMillis(shardLatency.p50.value), measure: quantityMeasure(shardLatency.p50) })}
          </p>
          <p className="sys-tile__sub">median write latency</p>
        </article>

        <article className="sys-tile" aria-label="Shard latency p95">
          <h3 className="sys-tile__label">Shard latency p95</h3>
          <p className="sys-tile__value">
            {shardLatency.p95.value === null
              ? "n/a"
              : formatMeasured({ value: formatMillis(shardLatency.p95.value), measure: quantityMeasure(shardLatency.p95) })}
          </p>
          <p className="sys-tile__sub">tail write latency</p>
        </article>

        <article className="sys-tile" aria-label="Hub connections">
          <h3 className="sys-tile__label">Hub connections</h3>
          <p className="sys-tile__value">{formatCount(hub.connections)}</p>
          <p className="sys-tile__sub">live stream subscribers</p>
        </article>

        <article className="sys-tile" aria-label="Verification lag">
          <h3 className="sys-tile__label">Verification lag</h3>
          <StatusBadge
            level={lagLevel}
            label={lagLabel}
            size="sm"
            title={
              verificationLagS === null
                ? "The verification lag could not be determined."
                : `Audit-chain verification is ${formatAgeSeconds(verificationLagS)} behind.`
            }
          />
          <p className="sys-tile__sub">
            {verificationLagS === null ? "no data" : `${formatAgeSeconds(verificationLagS)} behind`}
          </p>
        </article>
      </div>
    </section>
  );
}
